/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Feb 12 2025
 **/

import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { debounce } from "lodash-es";

import { Utility } from "@/frameworks/libraries";
import useDocumentViewerStore from "../../stores/useDocumentViewerStore";

export default function useDocumentViewerTransform(ref: React.RefObject<ReactZoomPanPinchRef>) {
	const scale = useDocumentViewerStore((state) => state.scale);
	const setScale = useDocumentViewerStore((state) => state.setScale);
	const minScale = useDocumentViewerStore((state) => state.minScale);
	const setMinScale = useDocumentViewerStore((state) => state.setMinScale);

	const initializeViewer = () => {
		// set scales
		const newScale = getScale();
		setScale(newScale);
		setMinScale(newScale);

		// change el
		updateViewerToCenter(newScale, 0);

		return getOriginalResolution(newScale);
	};

	const handleWheel = debounce((e: ReactZoomPanPinchRef) => {
		const roundScale = Utility.roundToDecimal(e.state.scale, 2);
		if (scale !== roundScale) setScale(Math.max(roundScale, minScale));
	}, 50);

	const updateViewerToCenter = (newScale: number = scale, animationTime: number = 200) => {
		ref.current?.centerView(newScale, animationTime);
	};

	const updatePanning = (delta: number) => {
		const instance = ref.current?.instance;
		if (instance && instance.bounds && instance.transformState) {
			const { minPositionY, maxPositionY } = instance.bounds;
			const { positionX, positionY, scale } = instance.transformState;
			const targetY = Math.min(Math.max(minPositionY, positionY - delta), maxPositionY);
			ref.current?.setTransform(positionX, targetY, scale);
		}
	};

	/* Utils */
	const getScale = () => {
		const transformWrapperEl = document.querySelector(".react-transform-wrapper");
		const transformComponentEl = document.querySelector(".react-transform-component");
		const wrapperWidth = transformWrapperEl?.clientWidth || window.innerWidth;
		const wrapperHeight = transformWrapperEl?.clientHeight || window.innerHeight;
		const componentWidth = transformComponentEl?.clientWidth || window.innerWidth;
		const componentHeight = transformComponentEl?.clientHeight || window.innerHeight;

		// 원본이 viewport보다 작다면 기본 스케일 반환
		if (wrapperWidth >= componentWidth && wrapperHeight >= componentHeight) return 1;
		// 원본이 viewport보다 크다면 fit하게 scale 조절
		const minScale = Math.min(wrapperWidth / componentWidth, wrapperHeight / componentHeight);
		return Utility.roundToDecimal(minScale, 2);
	};

	const getOriginalResolution = (newScale: number) => {
		const rect = document.querySelector(".react-transform-component")?.getBoundingClientRect();
		const rectWidth = rect?.width || window.innerWidth;
		const rectHeight = rect?.height || window.innerHeight;
		return { width: rectWidth / newScale, height: rectHeight / newScale };
	};

	return { initializeViewer, handleWheel, updateViewerToCenter, updatePanning };
}
